import { deleteDoc, doc, getDocs, orderBy, query, setDoc, where, writeBatch } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { activityLogsRef, ActivityLog } from '@api/ActivityLogs/ActivityLog';
import UserServ from '@api/Users/UserServ';
export default {
    /**
     * Registra un evento en la bitácora. **UNA escritura, no dos.**
     *
     * Antes hacía `addDoc` y enseguida un `updateDoc` cuyo único propósito era
     * meter el id que Firestore acababa de generar DENTRO del documento. Eso es
     * el doble de escrituras en la colección más grande del sistema: **183 mil
     * documentos y ~500 al día**, con los 7 puntos de pedidos aportando ~1,456
     * escrituras diarias por sí solos. `doc(ref)` genera el id **en el cliente,
     * sin ir a la red**, así que el documento puede nacer completo de una vez.
     *
     * ⚠️ El campo `id` DENTRO del documento se conserva a propósito: las
     * pantallas leen `doc.data()` y esperan encontrarlo ahí (`deleteActivityLog`
     * lo usa). Lo que se quitó es la segunda escritura, no el campo.
     */
    async addActivityLog(section: string, sectionId: string, description: string): Promise<void> {
        // ⚠️ **NUNCA LANZA.** Una bitácora es un observador: jamás puede ser la
        // causa de que algo no pase.
        //
        // Esto no es precaución teórica. En `completeOrder` esta llamada va
        // JUSTO DESPUÉS de escribir `status: Completed` y ANTES del bono de
        // bebidas, del aviso al cliente y de liberar la mesa — un corte de red
        // en la tablet mataba todo eso con el pedido ya marcado como
        // completado. Es la misma clase de incidente que ya se pagó el
        // 2026-08-19: el `throw` del ticket de WhatsApp se llevó por delante el
        // commit de inventario del 35 % de las ventas.
        //
        // Mismo patrón que el commit de inventario, el pase de Wallet y la
        // campana del cliente. Si el registro se pierde, se pierde un renglón
        // de historia; si la venta se rompe, se pierde dinero.
        try {
            const user = await UserServ.getUser();
            const ref = doc(activityLogsRef);
            const activityLog: ActivityLog = {
                id: ref.id,
                section,
                sectionId,
                name: user.profile.name,
                description,
                date: new Date()
            };
            await setDoc(ref, activityLog);
        } catch (error) {
            console.error('No se pudo registrar en la bitácora', { section, sectionId, description, error });
        }
    },
    /**
     * La bitácora de UN objeto. Los dos filtros son **obligatorios**, y eso es
     * un candado de costo, no una molestia: sin ellos la consulta no tiene
     * `limit` y traería **los 183 mil documentos** de la colección — una sola
     * llamada distraída costaría más lecturas que un día entero de operación.
     *
     * El único consumidor (`ActivityDrawer`) ya se guardaba de llamarla sin los
     * dos; ahora no depende de que el llamador se acuerde.
     */
    async getActivityLogs(section: string, sectionId: string): Promise<ActivityLog[]> {
        if (!section || !sectionId) return [];
        const queryRef = query(
            activityLogsRef,
            where('section', '==', section),
            where('sectionId', '==', sectionId),
            orderBy('date', 'desc')
        );
        const snapshot = await getDocs(queryRef);
        return snapshot.docs.map((doc) => doc.data() as ActivityLog);
    },
    async deleteActivityLog(activityLogId: string): Promise<void> {
        return await deleteDoc(doc(activityLogsRef, activityLogId));
    },
    async deleteActivityLogs(section: string, sectionId: string): Promise<void> {
        const batch = writeBatch(firestore);
        const queryDoc = query(activityLogsRef, where('section', '==', section), where('sectionId', '==', sectionId));
        const snapshot = await getDocs(queryDoc);
        snapshot.docs.map((doc) => {
            batch.delete(doc.ref);
        });
        await batch.commit();
        return;
    }
};
